<template>
  <div
    ref="containerEl"
    class="flowbuilder-node-container"
  >
    <BaseNode
      ref="nodeEl"
      :node="node"
      :icon="nodeIcon"
      :metrics="metrics"
      classes="bg-fuchsia-100 dark:bg-fuchsia-950 ring-fuchsia-400/50 dark:ring-fuchsia-500/50 does-terminate"
      @click="emit('click')"
      @delete-node="emit('delete-node')"
    >
      <template #context>
        Route by {{ node.mode === 'variables' ? 'highest variable' : 'high score' }}:
      </template>

      <div>
        Whichever {{ node.mode === 'variables' ? 'variable' : 'score' }} {{ listScores }} has the highest value will be used
      </div>
    </BaseNode>

    <div
      v-if="node.split && node.split.length > 0"
      class="flowbuilder-splits flowbuilder-splits-linear"
      @click.stop
    >
      <div
        v-for="(splitPath, splitIndex) in node.split"
        :key="splitIndex"
        class="flowbuilder-split"
      >
        <div
          ref="yesEl"
          class="flowbuilder-split-label"
        >
          {{ splitName(splitPath) }}
        </div>

        <NodeRun
          :nodes="splitPath.child"
          :parent-node="splitPath"
          :in-redirect-funnel="inRedirectFunnel"
          @nodes-updated="handleNodesUpdated(splitIndex, $event)"
          @node-action="(...args) => handleNodeAction(splitIndex, ...args)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, inject } from 'vue'
import BaseNode from './BaseNode.vue'
import NodeRun from './NodeRun.vue'
import { toSentence } from '@rightmessage/shared-utils'
import type { NodeHighScore } from '../../types/nodes'
import { getNodeIcon } from '../../utils/nodeIcons'

/**
 * HighScoreNode Component
 * 
 * Routes visitors based on which score has the highest value.
 * 
 * Features:
 * - Evaluates multiple scores and routes to highest
 * - Default fallback path if no scores match
 * - Terminal node (ends the current branch)
 * - Supports multiple score-based paths
 * 
 * Props:
 * - node: The high score node configuration with split paths
 * - icon: Optional custom icon
 * - CodeIcon: Default icon component for score nodes
 */

interface Props {
  node: NodeHighScore
  icon?: any
  CodeIcon: any
}

const props = defineProps<Props>()
const emit = defineEmits(['delete-node', 'click'])

const containerEl = ref<HTMLElement>()
const nodeEl = ref<any>()

/**
 * Generate list of scores being evaluated
 * Shows which scores are being compared
 */
const listScores = computed(() => {
  const scoreNames = props.node.split
    .filter((s) => s.score)
    .map((s) => s.score)
  return scoreNames.length > 0 ? '(' + toSentence(scoreNames) + ')' : ''
})

/**
 * Get display name for each split path
 */
const splitName = (splitPath: { score: string | null; child: any[] }) => {
  if (splitPath.score && splitPath.score !== '') return splitPath.score
  return 'Needs Configuring'
}

// Inject adapter for handling node actions
const adapter = inject('adapter') as any
const getNodeMetrics = inject<(node: any) => any>('getNodeMetrics', () => null)

// Inject inRedirectFunnel to pass down to child NodeRun components
const inRedirectFunnel = inject('inRedirectFunnel', false)

// Handle nodes update from child NodeRun
const handleNodesUpdated = (splitIndex: number, newNodes: any[]) => {
  if (props.node.split && props.node.split[splitIndex]) {
    props.node.split[splitIndex].child = newNodes
  }
}

// Handle node actions from child NodeRun
const handleNodeAction = (splitIndex: number, nodeIndex: number, action: string, data?: any) => {
  const splitPath = props.node.split?.[splitIndex]
  if (!splitPath) return

  const targetNode = splitPath.child[nodeIndex]
  if (!targetNode) return

  if (action === 'click' && adapter?.onNodeClick) {
    adapter.onNodeClick(targetNode)
  } else if (action === 'delete' && adapter?.onNodeDelete) {
    adapter.onNodeDelete(targetNode)
  }
}

// Get the appropriate icon for this node
const nodeIcon = computed(() => {
  // Fall back to iconify icons
  return getNodeIcon(props.node)
})

// Get metrics for this node
const metrics = computed(() => {
  return getNodeMetrics(props.node)
})

</script>
